import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { 
  getInspectionMeetings,
  getDistrict,
  getBlockByDistrict,
  getVillageByBlock,
  getAllMeetingDates
} from "../../api/User";
import loader from "../../Images/ajax-loader.gif";
import "../Admin/table.css";

const GramSabhaInspectionForms = ({ dateFilter }) => {
  const userId = sessionStorage.getItem("user_id");
  const role_id = sessionStorage.getItem("role_id");
  const token = sessionStorage.getItem("token");
  const navigate = useNavigate();

  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters State
  const [district, setDistrict] = useState("");
  const [block, setBlock] = useState("");
  const [gramPanchayat, setGramPanchayat] = useState("");
  const [searchDate, setSearchDate] = useState("");

  const [districtsList, setDistrictsList] = useState([]);
  const [blocksList, setBlocksList] = useState([]);
  const [panchayatsList, setPanchayatsList] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    const fetchDistricts = async () => {
      if (role_id === "1") {
        try {
          const res = await getDistrict(token, userId, role_id);
          if (res.status === "success" && res.data) setDistrictsList(res.data);
        } catch (error) { console.error(error); }
      }
    };
    fetchDistricts();
  }, [role_id, token, userId]);

  useEffect(() => {
    const fetchBlocks = async () => {
      if (role_id === "1" && !district) {
        setBlocksList([]);
        return;
      }
      if (["1", "7", "8"].includes(role_id)) {
        try {
          const res = await getBlockByDistrict(token, district, userId, role_id);
          if (res.status === "success" && res.data && res.data.length > 0) {
            setBlocksList(res.data);
            return;
          }
        } catch (error) { 
          console.warn("getBlockByDistrict API failed, falling back...");
        }

        // Fallback: Fetch Blocks from meeting data
        try {
          const fallbackFilters = {
            user_id: userId,
            role_id: role_id,
            meeting_type: "gram_sabha",
            district_code: district || null,
          };
          const fallbackRes = await getInspectionMeetings(token, fallbackFilters, 1, 1000);
          if (fallbackRes.status === "success" && fallbackRes.data) {
            const uniqueBlocks = [];
            const blockSet = new Set();
            fallbackRes.data.forEach(item => {
               const code = item.block_code || item.block_id || item.blk_code;
               const name = item.block_name || item.block || item.name;
               if (code && name && !blockSet.has(code)) {
                 blockSet.add(code);
                 uniqueBlocks.push({ block_code: code, block_name: name });
               }
            });
            setBlocksList(uniqueBlocks);
          }
        } catch (e) {
          console.error(e);
        }
      }
    };
    fetchBlocks();
  }, [district, role_id, token, userId]);

  useEffect(() => {
    const fetchGPs = async () => {
      if ((["1", "7", "8"].includes(role_id) && !block)) {
        setPanchayatsList([]);
        return;
      }
      if (["1", "3", "7", "8"].includes(role_id)) {
        try {
          const res = await getVillageByBlock(token, district, block, userId, role_id);
          if (res.status === "success" && res.data && res.data.length > 0) {
            setPanchayatsList(res.data);
            return;
          }
        } catch (error) { 
          console.warn("getVillageByBlock API failed, falling back..."); 
        }

        // Fallback: Fetch GPs from meeting data
        try {
          const fallbackFilters = {
            user_id: userId,
            role_id: role_id,
            meeting_type: "gram_sabha",
            district_code: district || null,
            block_code: block || null,
          };
          const fallbackRes = await getInspectionMeetings(token, fallbackFilters, 1, 1000);
          if (fallbackRes.status === "success" && fallbackRes.data) {
            const uniqueGPs = [];
            const gpSet = new Set();
            fallbackRes.data.forEach(item => {
               const code = item.gram_panchayat_code || item.gram_panchayat_id || item.panchayat_code;
               const name = item.panchayat_name || item.gram_panchayat || item.name;
               if (code && name && !gpSet.has(code)) {
                 gpSet.add(code);
                 uniqueGPs.push({ gram_panchayat_code: code, panchayat_name: name });
               }
            });
            setPanchayatsList(uniqueGPs);
          }
        } catch (e) {
          console.error(e);
        }
      }
    };
    fetchGPs();
  }, [block, district, role_id, token, userId]);

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const dateFilters = {
          role_id: role_id,
          user_id: userId,
          meeting_type: "gram_sabha",
          district_code: district || null,
          block_code: block || null,
          gram_panchayat_code: gramPanchayat || null,
        };
        try {
          const res = await getAllMeetingDates(token, dateFilters, dateFilter);
          if (res.status === "success" && res.data) {
            const uniqueDates = [...new Set(res.data.map(item => item.sabha_date ? new Date(item.sabha_date).toISOString().split("T")[0] : null))].filter(d => d).sort((a,b) => new Date(b) - new Date(a));
            setAvailableDates(uniqueDates);
            return;
          }
        } catch (e) {
          console.warn("getAllMeetingDates API failed, falling back...");
        }
        
        // Fallback
        const fallbackRes = await getInspectionMeetings(token, dateFilters, 1, 1000);
        if (fallbackRes.status === "success" && fallbackRes.data) {
          const uniqueDates = [...new Set(fallbackRes.data.map(item => item.sabha_date ? new Date(item.sabha_date).toISOString().split("T")[0] : null))].filter(d => d).sort((a,b) => new Date(b) - new Date(a));
          setAvailableDates(uniqueDates);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchDates();
  }, [district, block, gramPanchayat, role_id, token, userId, dateFilter]);

  const headerCellStyle = {
    padding: "16px",
    fontSize: "14px",
    fontWeight: "bold",
    whiteSpace: "normal",
    textAlign: "center",
    backgroundColor: "#2E4A8E",
    color: "white",
  };

  const cellStyle = {
    padding: "16px",
    fontSize: "14px",
    whiteSpace: "nowrap",
    textAlign: "center",
  };

  const fetchData = async (currentPage = page, currentLimit = rowsPerPage) => {
    try {
      setLoading(true);
      const data = {
        user_id: userId,
        role_id: role_id,
        district_code: district || null,
        block_code: block || null,
        gram_panchayat_code: gramPanchayat || null,
        sabha_date: searchDate || null,
        period: dateFilter || null,
      };
      const res = await getInspectionMeetings(token, data, currentPage + 1, currentLimit);
      if (res.status === "success" && res.data) {
        setFilteredData(res.data);
        setTotalRecords(res.pagination?.total_records || 0);
      } else {
        toast.error(res.message || "Failed to fetch meetings data", { autoClose: 2000 });
      }
    } catch (error) {
      console.error("Error fetching inspection meetings:", error);
      toast.error("An error occurred while fetching meetings data.", { autoClose: 2000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, district, block, gramPanchayat, searchDate, dateFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
  };

  const handleResetFilters = () => {
    setDistrict("");
    setBlock("");
    setGramPanchayat("");
    setSearchDate("");
    setPage(0);
  };

  const handleDevelopmentWork = (row) => {
    const meetingData = {
      district_code: row.district_code || row.district_id || row.district || row.dist_code,
      block_code: row.block_code || row.block_id || row.block || row.blk_code,
      gram_panchayat_code: row.gram_panchayat_code || row.gram_panchayat_id || row.panchayat_code,
      meeting_id: row.id || row.meeting_id || row.sched_meet_id,
      panchayat_name: row.panchayat_name,
      sabha_date: row.sabha_date,
      venue: row.venue,
    };
    sessionStorage.setItem("current_meeting", JSON.stringify(meetingData));
    navigate("/development-work", { state: meetingData });
  };

  const handleAmritSarovar = (row) => {
    const meetingData = {
      district_code: row.district_code || row.district_id || row.district || row.dist_code,
      block_code: row.block_code || row.block_id || row.block || row.blk_code,
      gram_panchayat_code: row.gram_panchayat_code || row.gram_panchayat_id || row.panchayat_code,
      meeting_id: row.id || row.meeting_id || row.sched_meet_id,
      panchayat_name: row.panchayat_name,
      sabha_date: row.sabha_date,
      venue: row.venue,
    };
    sessionStorage.setItem("current_meeting", JSON.stringify(meetingData));
    navigate("/amrit-sarovar", { state: meetingData });
  };

  const renderViewButton = (isFilled, onClickHandler) => {
    // If user is not Gram Sachiv (Role 2) and the form is NOT filled, disable the view button
    const isDisabled = role_id !== "2" && !isFilled;
    
    return (
      <span
        style={{
          color: isDisabled ? "#aaa" : "#4763E4",
          textDecoration: isDisabled ? "none" : "underline",
          cursor: isDisabled ? "not-allowed" : "pointer",
          fontWeight: "500"
        }}
        onClick={() => !isDisabled && onClickHandler()}
        title={isDisabled ? "Form not filled by Gram Sachiv yet" : ""}
      >
        View
      </span>
    );
  };

  return (
    <div className="container-fluid" style={{ height: "91vh", padding: "20px" }}>
      <ToastContainer />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginBottom: "10px" }}>
        <h2 style={{ margin: "0", fontWeight: "bold" }}>
          Gram Sabha Agenda Points
        </h2>
        
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          {role_id === "1" && (
            <select className="form-select dropdown-custom" style={{ width: "160px", height: "34px" }} value={district} onChange={(e) => { setDistrict(e.target.value); setBlock(""); setGramPanchayat(""); setSearchDate(""); }}>
              <option value="">District</option>
              {districtsList.map((d, i) => <option key={i} value={d.district_code || d.dist_code || d.id}>{d.district || d.district_name || d.name}</option>)}
            </select>
          )}
          {["1", "7", "8"].includes(role_id) && (
            <select 
              className="form-select dropdown-custom" 
              style={{ width: "160px", height: "34px" }} 
              value={block} 
              onChange={(e) => { setBlock(e.target.value); setGramPanchayat(""); setSearchDate(""); }}
              disabled={role_id === "1" && !district}
            >
              <option value="">Block</option>
              {blocksList.map((b, i) => <option key={i} value={b.block_code || b.blk_code || b.id}>{b.block || b.block_name || b.name}</option>)}
            </select>
          )}
          {["1", "3", "7", "8"].includes(role_id) && (
            <select 
              className="form-select dropdown-custom" 
              style={{ width: "180px", height: "34px" }} 
              value={gramPanchayat} 
              onChange={(e) => { setGramPanchayat(e.target.value); setSearchDate(""); }}
              disabled={["1", "7", "8"].includes(role_id) && !block}
            >
              <option value="">Gram Panchayat</option>
              {panchayatsList.map((p, i) => <option key={i} value={p.gram_panchayat_code || p.panchayat_code || p.id}>{p.gram_panchayat || p.panchayat_name || p.name}</option>)}
            </select>
          )}
          {["1", "3", "7", "8"].includes(role_id) && (
            <>
              <select className="form-select dropdown-custom" style={{ width: "160px", height: "34px" }} value={searchDate} onChange={(e) => setSearchDate(e.target.value)}>
                <option value="">Select Date</option>
                {availableDates.map((d, i) => {
                  const parts = d.split("-");
                  const formatted = parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
                  return <option key={i} value={d}>{formatted}</option>;
                })}
              </select>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleResetFilters} 
                style={{ height: "34px", textTransform: "none" }}
              >
                Reset
              </Button>
            </>
          )}
        </div>
      </div>
      <hr style={{ margin: "0 0 20px 0", border: "2px solid #ddd" }} />

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <img src={loader} alt="loading..." />
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <Table aria-label="simple table">
              <TableHead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "#2E4A8E",
                }}
              >
                <TableRow>
                  <TableCell style={headerCellStyle} align="center">Sr. No.</TableCell>
                  <TableCell style={headerCellStyle} align="center">Gram Panchayat</TableCell>
                  <TableCell style={headerCellStyle} align="center">Date</TableCell>
                  <TableCell style={headerCellStyle} align="center">Time</TableCell>
                  <TableCell style={headerCellStyle} align="center">Venue</TableCell>
                  <TableCell style={headerCellStyle} align="center">Development Work</TableCell>
                  <TableCell style={headerCellStyle} align="center">Amrit Sarovar</TableCell>
                  {dateFilter !== "before" && (
                    <>
                      <TableCell style={headerCellStyle} align="center">Village Drinking Water Supply Status</TableCell>
                      <TableCell style={headerCellStyle} align="center">Status of Drug Addiction</TableCell>
                      <TableCell style={headerCellStyle} align="center">Cleanliness, Solid Waste Management, Inspection of Government Building</TableCell>
                      <TableCell style={headerCellStyle} align="center">Wastewater Management</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={dateFilter === "before" ? 7 : 11} align="center" style={{ padding: "30px", color: "#666" }}>
                      No Gram Sabha meetings found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((row, index) => (
                    <TableRow 
                      key={row.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#fff" : "#f8fafc"
                      }}
                    >
                      <TableCell style={cellStyle} align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell style={{ ...cellStyle, fontWeight: "500" }} align="center">{row.panchayat_name}</TableCell>
                      <TableCell style={cellStyle} align="center">{row.sabha_date}</TableCell>
                      <TableCell style={cellStyle} align="center">{row.sabha_start_time ? row.sabha_start_time.substring(0, 5) : ""}</TableCell>
                      <TableCell style={cellStyle} align="center">{row.venue}</TableCell>

                      {/* Development Work Link */}
                      <TableCell style={cellStyle} align="center">
                        {renderViewButton(
                          row.is_development_work_filled, 
                          () => handleDevelopmentWork(row)
                        )}
                      </TableCell>

                      {/* Amrit Sarovar Link */}
                      <TableCell style={cellStyle} align="center">
                        {renderViewButton(
                          row.is_amrit_sarovar_filled,
                          () => handleAmritSarovar(row)
                        )}
                      </TableCell>
                      
                      {dateFilter !== "before" && (
                        <>
                          {/* Drinking Water Link */}
                          <TableCell style={cellStyle} align="center">
                            {renderViewButton(
                              row.is_drinking_water_filled,
                              () => navigate(`/gram-sabha-inspection/drinking-water/${row.id}?gp=${row.gram_panchayat_id}`)
                            )}
                          </TableCell>

                          {/* Drug Addiction Link */}
                          <TableCell style={cellStyle} align="center">
                            {renderViewButton(
                              row.is_drug_addiction_filled,
                              () => navigate(`/gram-sabha-inspection/drug-addiction/${row.id}?gp=${row.gram_panchayat_id}`)
                            )}
                          </TableCell>

                          {/* Cleanliness/SWM Link */}
                          <TableCell style={cellStyle} align="center">
                            {renderViewButton(
                              row.is_cleanliness_filled,
                              () => navigate(`/gram-sabha-inspection/cleanliness/${row.id}?gp=${row.gram_panchayat_id}`)
                            )}
                          </TableCell>

                          {/* Wastewater Link */}
                          <TableCell style={cellStyle} align="center">
                            {renderViewButton(
                              row.is_wastewater_filled,
                              () => navigate(`/gram-sabha-inspection/wastewater/${row.id}?gp=${row.gram_panchayat_id}`)
                            )}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {filteredData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default GramSabhaInspectionForms;
